import * as $ from 'jquery';

// Define FAQ schema interfaces
interface FaqAnswerSchema {
    "@type": "Answer";
    "text": string;
}

interface FaqQuestionSchema {
    "@type": "Question";
    "name": string;
    "acceptedAnswer": FaqAnswerSchema;
}

interface FaqPageSchema {
    "@context": "https://schema.org";
    "@type": "FAQs";
    "mainEntity": FaqQuestionSchema[];
}

// Initialize on document ready
document.addEventListener('DOMContentLoaded', function () {
    initAccordions();
});

/**
 * Initialize all accordion groups on the page
 */
function initAccordions(): void {
    const $accordionGroups = $('.accordionGroup');

    if ($accordionGroups.length === 0) return;

    $accordionGroups.each(function (groupIndex) {
        const $group = $(this);
        setupAccordionGroup($group, groupIndex);
        
        // Generate and add JSON-LD schema for this accordion group
        generateFaqSchema($group);
    });
}

/**
 * Generate JSON-LD schema for FAQ accordion and add it to the page
 */
function generateFaqSchema($group: JQuery<HTMLElement>): void {
    const faqData: FaqQuestionSchema[] = [];
    const $accordions = $group.find('.accordion');
    
    // Exit if no accordions found
    if ($accordions.length === 0) return;
    
    // Collect data from each accordion
    $accordions.each(function() {
        const $accordion = $(this);
        const $heading = $accordion.find('.accordion__heading');
        const $content = $accordion.find('.accordion__content');
        
        // Get the question text (remove any HTML)
        const questionText = $heading.clone()
            .children()
            .remove()
            .end()
            .text()
            .trim();
        
        // Get the answer HTML as a string
        const answerHtml = $content.html()?.trim() || '';
        
        // Skip if either question or answer is empty
        if (!questionText || !answerHtml) return;
        
        // Add to FAQ data array
        faqData.push({
            "@type": "Question",
            "name": questionText,
            "acceptedAnswer": {
                "@type": "Answer",
                "text": answerHtml
            }
        });
    });
    
    // Skip if no valid FAQ items found
    if (faqData.length === 0) return;
    
    // Create the complete JSON-LD structure
    const jsonLdData: FaqPageSchema = {
        "@context": "https://schema.org",
        "@type": "FAQs",
        "mainEntity": faqData
    };
    
    // Create script element
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(jsonLdData);
    
    // Remove any existing JSON-LD script in this group to prevent duplicates
    $group.find('script[type="application/ld+json"]').remove();
    
    // Append the script to the accordion group element
    $group.append(script);
}

/**
 * Set up a single accordion group
 */
function setupAccordionGroup($group: JQuery<HTMLElement>, groupIndex: number): void {
    // Close all accordions on page load
    const $accordions = $group.find('.accordion');
    $accordions.removeClass('active').find('.accordion__content').slideUp(0);
    
    // Add role to the accordion group
    $group.attr('role', 'presentation');
    
    // Set 'odd' or 'even' accordion count as a data attribute
    const accordionCount = $accordions.length;
    $group.attr('data-accordion-count', accordionCount % 2 === 0 ? 'even' : 'odd');

    // Set up each accordion in the group
    $accordions.each(function (accordionIndex) {
        const $accordion = $(this);
        const $heading = $accordion.find('.accordion__heading');
        const $content = $accordion.find('.accordion__content');
        
        // Add the position index as a data attribute (1-based for easier CSS selectors)
        $accordion.attr('data-position', (accordionIndex + 1).toString());
        
        // Generate unique IDs
        const headingId = `accordion-heading-${groupIndex}-${accordionIndex}`;
        const contentId = `accordion-content-${groupIndex}-${accordionIndex}`;
        // Generate unique ID for the accordion element for URL hash functionality remove the last hyphen if it exists, also ensure it remains unique in case there are multiple accordions with the same question
        const accordionId = $heading.text().replace(/[^a-zA-Z0-9]/g, '-').toLowerCase().replace(/-$/, '').replace(/-+/g, '-');
        
        // Set unique ID for the accordion element for URL hash functionality
        $accordion.attr('id', accordionId);
        
        // Store question ID and add control element
        const questionText = $heading.text();
        $accordion.attr('data-question-id', questionText);
        
        // Add control element if it doesn't exist
        if ($heading.find('.accordion__control').length === 0) {
            $heading.append('<span class="accordion__control"></span>');
        }
        
        // Add ARIA attributes
        $heading.attr({
            'id': headingId,
            'role': 'button',
            'aria-expanded': 'false',
            'aria-controls': contentId,
            'tabindex': '0'
        });
        
        $content.attr({
            'id': contentId,
            'role': 'region',
            'aria-labelledby': headingId,
            'aria-hidden': 'true'
        });
    });

    // Check if there's a hash in the URL on page load and open the corresponding accordion
    if (window.location.hash) {
        const hashId = window.location.hash.substring(1);
        const $targetAccordion = $group.find(`#${hashId}`);
        if ($targetAccordion.length) {
            const questionId = $targetAccordion.attr('data-question-id');
            if (questionId) {
                // Use setTimeout to ensure DOM is ready
                setTimeout(() => {
                    $group.trigger('changeActive', { questionId });
                }, 100);
            }
        }
    }

    // Set up click listeners
    $group.find('.accordion__heading').off('click keydown').on('click', function () {
        const questionId = $(this).closest('.accordion').attr('data-question-id');
        $group.trigger('changeActive', { questionId });
    }).on('keydown', function(e) {
        // Add keyboard accessibility
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            const questionId = $(this).closest('.accordion').attr('data-question-id');
            $group.trigger('changeActive', { questionId });
        }
    });

    // Handle active state changes
    $group.off('changeActive').on('changeActive', function (event: JQuery.TriggeredEvent, data: { questionId: string }) {
        const $targetAccordion = $group.find(`.accordion[data-question-id="${data.questionId}"]`);
        const $targetHeading = $targetAccordion.find('.accordion__heading');
        const $targetContent = $targetAccordion.find('.accordion__content');
        const isExpanded = $targetAccordion.hasClass('active');
        
        // Toggle the clicked accordion
        if (isExpanded) {
            $targetAccordion.removeClass('active');
            $targetContent.slideUp();
            
            // Update ARIA attributes
            $targetHeading.attr('aria-expanded', 'false');
            $targetContent.attr('aria-hidden', 'true');
            
            // Update URL hash - remove hash if closing this accordion
            if (window.location.hash && window.location.hash.substring(1) === $targetAccordion.attr('id')) {
                const url = new URL(window.location.href);
                // Check if there are other open accordions to set as the new hash
                const $openAccordion = $group.find('.accordion.active').first();
                url.hash = $openAccordion.length ? $openAccordion.attr('id') || '' : '';
                history.replaceState(history.state, '', url.toString());
            }
        } else {
            $targetAccordion.addClass('active');
            $targetContent.slideDown();
            
            // Update ARIA attributes
            $targetHeading.attr('aria-expanded', 'true');
            $targetContent.attr('aria-hidden', 'false');
            
            // Update URL hash - set hash to the opened accordion's ID
            const url = new URL(window.location.href);
            url.hash = $targetAccordion.attr('id') || '';
            history.replaceState(history.state, '', url.toString());
            
            // To have only one accordion open at a time, uncomment the block below
            /* 
            $accordions.not($targetAccordion).each(function() {
                const $accordion = $(this);
                $accordion.removeClass('active');
                $accordion.find('.accordion__content').slideUp();
                $accordion.find('.accordion__heading').attr('aria-expanded', 'false');
                $accordion.find('.accordion__content').attr('aria-hidden', 'true');
            });
            */
        }
    });
}
